import React, {Fragment, Component} from 'react';
import {compose, withApollo} from 'react-apollo';
import {FormattedMessage} from 'react-intl';
import Modal from '@homeaway/react-modal';
import {createMultiUnitSearchQuery, setBaseVariables, setVariables} from '../utils/multiUnitQuery';
import MultiUnitTypesSummary from '../MultiUnitTypesSummary/MultiUnitTypesSummary';
import MultiUnitLoader from '../MultiUnitLoader/MultiUnitLoader';
import MultiUnitListing from '../MultiUnitListing/MultiUnitListing';
import {prepareMultiUnitListState} from '../utils/multiUnitData';
import './MultiUnitListWrapper.less';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

const wrapWithModal = ({
    content,
    loading,
    resultCount,
    isOpen,
    onModalClose,
    propertyName,
    minPriceData
}) => {
    return (
        <Modal
            className="MultiUnit__modal"
            id="multiUnitModal"
            isOpen={isOpen}
            onClose={onModalClose}
            modalAnimation="modal-from-bottom"
            isWorkflow
            aria-label={propertyName}
            title={
                <MultiUnitTypesSummary
                    resultCount={resultCount}
                    propertyName={propertyName}
                    loading={loading}
                    minPriceData={minPriceData}
                />
            }
        >
            <ul className="MultiUnitModal__content">{content}</ul>
        </Modal>
    );
};

const multiUnitList = ({
    loading,
    propertyName,
    listings,
    unfilteredListings,
    resultCount,
    unfilteredResultCount,
    isDatelessSearch,
    isMetric,
    propertyId,
    showDesktopView,
    context,
    minPriceData,
    clientLogger
}) => {
    return (
        <div className="MultiUnitListWrapper">
            {loading && (
                <Fragment>
                    {showDesktopView && (
                        <MultiUnitTypesSummary propertyName={propertyName} loading />
                    )}
                    <MultiUnitLoader multiUnitDataCount={showDesktopView ? 4 : 8} />
                </Fragment>
            )}
            {!!listings.length && (
                <div>
                    {showDesktopView && (
                        <MultiUnitTypesSummary
                            propertyName={propertyName}
                            resultCount={resultCount}
                            loading={false}
                            minPriceData={minPriceData}
                        />
                    )}
                    <MultiUnitListing
                        context={context}
                        isDatelessSearch={isDatelessSearch}
                        isMetric={isMetric}
                        multiUnitList={{
                            multiUnitSearch: {listings}
                        }}
                        propertyId={propertyId}
                        clientLogger={clientLogger}
                    />
                    {!!unfilteredListings.length && (
                        <Fragment>
                            <hr />
                            <span className="MultiUnitListWrapper__unfiltered-results-title micro">
                                <FormattedMessage
                                    id="multipleUnitsNotMatch"
                                    defaultMessage="{count, plural, one {{count} unit at this property does not match} few {{count} units at this property do not match} many {{count} units at this property do not match} other {{count} units at this property do not match}} your dates or filters:"
                                    values={{
                                        count: unfilteredResultCount
                                    }}
                                >
                                    {(txt) => txt}
                                </FormattedMessage>
                            </span>
                            <MultiUnitListing
                                context={context}
                                isDatelessSearch={false}
                                isMetric={isMetric}
                                multiUnitList={{
                                    multiUnitSearch: {
                                        listings: unfilteredListings
                                    }
                                }}
                                propertyId={propertyId}
                                clientLogger={clientLogger}
                            />
                        </Fragment>
                    )}
                </div>
            )}
        </div>
    );
};

class MultiUnitListWrapper extends Component {
    constructor(props) {
        super(props);

        this.state = {
            listings: [],
            loading: true,
            resultCount: 0,
            unfilteredResultCount: 0,
            unfilteredListings: [],
            minPriceData: {
                isPriceRange: false,
                price: '$0'
            },
            isError: false
        };
    }

    componentDidMount() {
        this.fetchMultiUnitListings();
    }

    fetchMultiUnitListings() {
        const {client, currency, inputParams, listing, clientLogger} = this.props;
        const {listingNamespace: systemId, listingNumber: propertyId} = listing;
        const query = createMultiUnitSearchQuery(currency);

        Promise.all([
            client.query({
                query,
                variables: setVariables(inputParams, systemId, propertyId),
                errorPolicy: 'all'
            }),
            client.query({
                query,
                variables: setBaseVariables(systemId, propertyId),
                errorPolicy: 'all'
            })
        ])
            .then((responses) => {
                if (responses.every((collection) => !collection.data.multiUnitSearch.listings.length)) {
                    clientLogger.captureException('[MultiUnitSearchQuery error]: No data', {
                        extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                    });
                    this.setState({isError: true});
                } else {
                    this.setState(prepareMultiUnitListState(responses, listing));
                }
            })
            .catch((e) => {
                this.setState({isError: true});
                clientLogger.captureException(`[MultiUnitSearchQuery error]: ${e}`, {
                    extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                });
            });
    }
    render() {
        const {
            listings,
            resultCount,
            unfilteredListings,
            unfilteredResultCount,
            loading,
            minPriceData
        } = this.state;
        const {
            isMetric,
            isDatelessSearch,
            propertyName,
            showDesktopView,
            isOpen,
            onDropdownHide,
            onModalClose,
            context,
            clientLogger
        } = this.props;
        const {listingNumber: propertyId} = this.props.listing;

        if (this.state.isError) {
            return showDesktopView ? (
                <ErrorMessage onClose={onDropdownHide} />
            ) : (
                wrapWithModal({
                    isOpen,
                    resultCount,
                    loading: false,
                    onModalClose,
                    propertyName,
                    minPriceData,
                    content: <ErrorMessage onClose={onModalClose} />
                })
            );
        }

        const content = multiUnitList({
            listings,
            resultCount,
            unfilteredListings,
            unfilteredResultCount,
            loading,
            isMetric,
            isDatelessSearch,
            propertyId,
            propertyName,
            showDesktopView,
            context,
            minPriceData,
            clientLogger
        });

        return showDesktopView
            ? content
            : wrapWithModal({
                isOpen,
                resultCount,
                loading,
                onModalClose,
                propertyName,
                content,
                minPriceData
            });
    }
}

export default compose(withApollo)(MultiUnitListWrapper);
